import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';
import { merchantApi } from '../../api/config';
import { toast } from 'react-toastify';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCoins, faArrowDown, faArrowUp, faChartBar, faUniversity,
  faArrowLeft, faBan, faSpinner, faMoneyBillTransfer,
} from '@fortawesome/free-solid-svg-icons';
import DataTable, { Column } from '../../components/shared/DataTable';
import {
  UiPageHeader,
  UiFilterPanel,
  UiStatsGrid,
  UiButton,
} from '../../components/ui';
import type { StatCardDef } from '../../components/ui';

dayjs.extend(utc);
dayjs.extend(timezone);

const BKK = 'Asia/Bangkok';
const getFirstDayOfMonth = () => dayjs().tz(BKK).startOf('month').format('YYYY-MM-DD');
const getTodayStr        = () => dayjs().tz(BKK).format('YYYY-MM-DD');

const fmt = (n: number) =>
  n.toLocaleString('th-TH', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const isCredit = (type: string) => type === 'credit' || type === 'in';

const bankCodeColors: Record<string, string> = {
  SCB:   'bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-400',
  KBANK: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400',
  BBL:   'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400',
  KTB:   'bg-sky-100 dark:bg-sky-900/30 text-sky-700 dark:text-sky-400',
  BAY:   'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-400',
  GSB:   'bg-pink-100 dark:bg-pink-900/30 text-pink-700 dark:text-pink-400',
};

interface AgentInfo {
  id: string;
  name: string;
  email: string;
  deposit_commission_rate: number;
  withdrawal_commission_rate: number;
  is_active: boolean;
}

interface BankRow {
  bank: { id: string; name: string; bank_code: string; account_number: string } | null;
  total_deposit: number;
  total_withdrawal: number;
  deposit_commission: number;
  withdrawal_commission: number;
  total_commission: number;
}

interface ReportSummary {
  total_deposit: number;
  total_withdrawal: number;
  total_deposit_commission: number;
  total_withdrawal_commission: number;
  total_commission: number;
}

interface Bank {
  id: string;
  name: string;
  bank_code: string;
  account_number: string;
}

interface Transaction {
  id: string;
  bank_id: string;
  transaction_type: 'credit' | 'debit' | 'in' | 'out';
  amount: number;
  from_account: string | null;
  transaction_date: string;
  reference_id: string | null;
  sender: string | null;
  banks: { name: string; bank_code: string } | null;
}

interface TxSummary {
  total_credit: number;
  credit_count: number;
  total_debit: number;
  debit_count: number;
  matched_count: number;
}

const emptyReportSummary: ReportSummary = {
  total_deposit: 0, total_withdrawal: 0,
  total_deposit_commission: 0, total_withdrawal_commission: 0, total_commission: 0,
};

const AgentCommissionDetailPage: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams<{ id: string }>();

  // กลับไปหน้าที่มา (agent-banks หรือ agent-commissions) — เปิดลิงก์ตรงไม่มี history → ไปหน้า list
  const handleBack = () => {
    if (location.key !== 'default') navigate(-1);
    else navigate('/admin/agent-commissions');
  };

  const [agent, setAgent] = useState<AgentInfo | null>(null);
  const [reportRows, setReportRows] = useState<BankRow[]>([]);
  const [reportSummary, setReportSummary] = useState<ReportSummary>(emptyReportSummary);
  const [reportLoading, setReportLoading] = useState(true);

  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [banks, setBanks] = useState<Bank[]>([]);
  const [txSummary, setTxSummary] = useState<TxSummary | null>(null);
  const [txLoading, setTxLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);

  const [dateFrom, setDateFrom] = useState(getFirstDayOfMonth());
  const [dateTo, setDateTo] = useState(getTodayStr());
  const [selectedBankId, setSelectedBankId] = useState('');
  // filter ที่ "กดค้นหาแล้ว" — pagination ต้องใช้ค่านี้ ไม่ใช่ค่าที่กำลังพิมพ์ค้าง (กันสองตารางเหลื่อมช่วงกัน)
  const [applied, setApplied] = useState(() => ({
    from: getFirstDayOfMonth(), to: getTodayStr(), bankId: '',
  }));

  const adminUser = JSON.parse(localStorage.getItem('admin_user') || 'null');
  const isAdminUser = adminUser?.type === 'admin' || !adminUser?.type;

  // seq กัน response เก่าที่มาช้าทับผลของ search/reset ที่ใหม่กว่า
  const reportSeq = useRef(0);
  const txSeq = useRef(0);

  const fetchReport = useCallback(async (from: string, to: string) => {
    if (!id) return;
    const seq = ++reportSeq.current;
    setReportLoading(true);
    try {
      const res = await merchantApi.get(`/admin/agent-banks/${id}/report`, {
        params: { date_from: from, date_to: to },
      });
      if (seq !== reportSeq.current) return;
      if (res.data.success) {
        setAgent(res.data.data.agent);
        setReportRows(res.data.data.rows);
        setReportSummary(res.data.data.summary);
      }
    } catch (err: any) {
      if (seq !== reportSeq.current) return;
      if (err.response?.status === 404) {
        toast.error('ไม่พบ Agent Bank');
        navigate('/admin/agent-commissions');
        return;
      }
      toast.error('ดึงข้อมูล report ไม่สำเร็จ');
    } finally {
      if (seq === reportSeq.current) setReportLoading(false);
    }
  }, [id, navigate]);

  // withSummary=true เฉพาะตอน search/reset (เปลี่ยนหน้าไม่ต้องโหลด summary ซ้ำ)
  const fetchTransactions = useCallback(async (
    p: number, from: string, to: string, bankId: string, limit?: number, withSummary = false
  ) => {
    if (!id) return;
    const seq = ++txSeq.current;
    setTxLoading(true);
    try {
      const res = await merchantApi.get(`/admin/agent-banks/${id}/transactions`, {
        params: {
          date_from: from,
          date_to: to,
          bank_id: bankId || undefined,
          page: p,
          limit: limit ?? pageSize,
          include_summary: withSummary ? 'true' : undefined,
        },
      });
      if (seq !== txSeq.current) return;
      if (res.data.success) {
        setTransactions(res.data.data.transactions);
        setBanks(res.data.data.banks);
        setTotal(res.data.data.total);
        setPage(p);
        if (res.data.data.summary) setTxSummary(res.data.data.summary);
      }
    } catch (err: any) {
      if (seq !== txSeq.current) return;
      if (err.response?.status !== 404) toast.error('ดึงข้อมูลธุรกรรมไม่สำเร็จ');
    } finally {
      if (seq === txSeq.current) setTxLoading(false);
    }
  }, [id, pageSize]);

  useEffect(() => {
    const f = getFirstDayOfMonth();
    const t = getTodayStr();
    setDateFrom(f);
    setDateTo(t);
    setSelectedBankId('');
    setApplied({ from: f, to: t, bankId: '' });
    fetchReport(f, t);
    fetchTransactions(1, f, t, '', undefined, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleSearch = () => {
    if (dayjs(dateFrom).isAfter(dayjs(dateTo), 'day')) {
      toast.error('วันที่เริ่มต้นต้องไม่เกินวันที่สิ้นสุด');
      return;
    }
    setApplied({ from: dateFrom, to: dateTo, bankId: selectedBankId });
    fetchReport(dateFrom, dateTo);
    fetchTransactions(1, dateFrom, dateTo, selectedBankId, undefined, true);
  };

  const handleReset = () => {
    const f = getFirstDayOfMonth();
    const t = getTodayStr();
    setDateFrom(f);
    setDateTo(t);
    setSelectedBankId('');
    setApplied({ from: f, to: t, bankId: '' });
    fetchReport(f, t);
    fetchTransactions(1, f, t, '', undefined, true);
  };

  const statCards: StatCardDef[] = useMemo(() => [
    {
      icon: faArrowDown,
      iconClassName: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
      label: 'ยอดฝากรวม',
      value: <span className="text-green-600 dark:text-green-400">฿{fmt(reportSummary.total_deposit)}</span>,
    },
    {
      icon: faArrowUp,
      iconClassName: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
      label: 'ยอดถอนรวม',
      value: <span className="text-amber-600 dark:text-amber-400">฿{fmt(reportSummary.total_withdrawal)}</span>,
    },
    {
      icon: faCoins,
      iconClassName: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
      label: 'ค่าคอมฝาก',
      value: <span className="text-blue-600 dark:text-blue-400">฿{fmt(reportSummary.total_deposit_commission)}</span>,
    },
    {
      icon: faCoins,
      iconClassName: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400',
      label: 'ค่าคอมถอน',
      value: <span className="text-purple-600 dark:text-purple-400">฿{fmt(reportSummary.total_withdrawal_commission)}</span>,
    },
    {
      icon: faChartBar,
      iconClassName: 'text-white',
      iconStyle: { background: 'var(--theme-primary-gradient)' },
      label: 'รวมค่าคอมทั้งหมด',
      value: <span style={{ color: 'var(--theme-primary)' }}>฿{fmt(reportSummary.total_commission)}</span>,
    },
  ], [reportSummary]);

  const txColumns: Column<Transaction>[] = useMemo(() => [
    {
      key: 'transaction_date',
      header: 'วันที่/เวลา',
      width: '130px',
      render: (tx) => (
        <div>
          <p className="text-sm text-gray-700 dark:text-slate-200 font-medium">{dayjs(tx.transaction_date).format('DD/MM/YYYY')}</p>
          <p className="text-xs text-gray-400 dark:text-slate-500">{dayjs(tx.transaction_date).format('HH:mm:ss')}</p>
        </div>
      ),
    },
    {
      key: 'bank',
      header: 'ธนาคาร',
      render: (tx) => tx.banks ? (
        <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${bankCodeColors[tx.banks.bank_code] || 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-400'}`}>
          {tx.banks.bank_code}
        </span>
      ) : <span className="text-gray-400 dark:text-slate-500 text-xs">—</span>,
    },
    {
      key: 'transaction_type',
      header: 'ประเภท',
      render: (tx) => (
        <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-lg text-xs font-semibold ${
          isCredit(tx.transaction_type)
            ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400'
            : 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400'
        }`}>
          <FontAwesomeIcon icon={isCredit(tx.transaction_type) ? faArrowDown : faArrowUp} className="text-[10px]" />
          {isCredit(tx.transaction_type) ? 'ฝาก' : 'ถอน'}
        </span>
      ),
    },
    {
      key: 'amount',
      header: 'จำนวนเงิน',
      headerClassName: 'text-right',
      className: 'text-right',
      render: (tx) => (
        <span className={`font-bold ${isCredit(tx.transaction_type) ? 'text-green-600 dark:text-green-400' : 'text-amber-600 dark:text-amber-400'}`}>
          ฿{fmt(Number(tx.amount))}
        </span>
      ),
    },
    {
      key: 'from_account',
      header: 'ต้นทาง',
      render: (tx) => (
        <div>
          {tx.sender && <p className="text-sm text-gray-700 dark:text-slate-200 truncate max-w-[140px]">{tx.sender}</p>}
          {tx.from_account && <p className="text-xs text-gray-400 dark:text-slate-500">{tx.from_account}</p>}
          {!tx.sender && !tx.from_account && <span className="text-gray-400 dark:text-slate-500 text-xs">—</span>}
        </div>
      ),
    },
    {
      key: 'reference_id',
      header: 'อ้างอิง',
      render: (tx) => tx.reference_id
        ? <span className="text-xs text-gray-500 dark:text-slate-400 font-mono">{tx.reference_id}</span>
        : <span className="text-gray-400 dark:text-slate-500 text-xs">—</span>,
    },
  ], []);

  if (!isAdminUser) {
    return (
      <div className="p-8 text-center text-gray-500 dark:text-slate-400">
        <FontAwesomeIcon icon={faBan} className="text-4xl text-red-300 dark:text-red-500/50 mb-3" />
        <p>ไม่มีสิทธิ์เข้าถึงหน้านี้ — เฉพาะ Admin เท่านั้น</p>
      </div>
    );
  }

  return (
    <div className="max-w-full">
      <UiPageHeader
        icon={faCoins}
        title={agent ? `ค่าคอม: ${agent.name}` : 'ค่าคอม Agent'}
        subtitle={
          agent
            ? `${agent.email} • เรทฝาก ${agent.deposit_commission_rate}% · เรทถอน ${agent.withdrawal_commission_rate}%${agent.is_active ? '' : ' • ปิดใช้งานอยู่'}`
            : 'รายละเอียดค่าคอมมิชชั่นและธุรกรรม'
        }
        actions={
          <UiButton icon={faArrowLeft} onClick={handleBack}>
            กลับ
          </UiButton>
        }
      />

      <UiFilterPanel
        onSearch={handleSearch}
        onReset={handleReset}
        loading={reportLoading || txLoading}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
      >
        <div className="w-[260px] shrink-0">
          <label className="block text-xs font-medium text-gray-500 dark:text-slate-400 mb-1">ธนาคาร (เฉพาะตารางธุรกรรม)</label>
          <select
            value={selectedBankId}
            onChange={(e) => setSelectedBankId(e.target.value)}
            className="w-full px-3 py-2 border border-gray-200 dark:border-slate-600 dark:bg-slate-700 dark:text-slate-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 dark:focus:ring-slate-600 focus:border-gray-400 dark:focus:border-slate-500"
          >
            <option value="">ทุกธนาคาร</option>
            {banks.map((bank) => (
              <option key={bank.id} value={bank.id}>
                [{bank.bank_code}] {bank.name} — {bank.account_number}
              </option>
            ))}
          </select>
        </div>
      </UiFilterPanel>

      <UiStatsGrid cards={statCards} loading={reportLoading} cols={5} />

      {/* รายการตามธนาคาร */}
      <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-theme overflow-hidden mb-6">
        <div className="px-5 py-4 border-b border-gray-100 dark:border-slate-700 flex items-center gap-3">
          <FontAwesomeIcon icon={faUniversity} style={{ color: 'var(--theme-primary)' }} />
          <h2 className="font-semibold text-gray-800 dark:text-slate-100">รายการตามธนาคาร</h2>
          <span className="ml-auto text-xs text-gray-400 dark:text-slate-500">{reportRows.length} รายการ</span>
        </div>

        {reportLoading ? (
          <div className="flex items-center justify-center py-12 gap-3">
            <FontAwesomeIcon icon={faSpinner} className="animate-spin text-xl" style={{ color: 'var(--theme-primary)' }} />
            <span className="text-gray-500 dark:text-slate-400 text-sm">กำลังโหลด...</span>
          </div>
        ) : reportRows.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <FontAwesomeIcon icon={faChartBar} className="text-gray-300 dark:text-slate-600 text-4xl mb-3" />
            <p className="text-gray-500 dark:text-slate-400 font-medium">ไม่มีข้อมูลในช่วงเวลาที่เลือก</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 dark:border-slate-700 bg-gray-50 dark:bg-slate-700/40">
                  <th className="px-5 py-3.5 text-left text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">ธนาคาร</th>
                  <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">ยอดฝากรวม</th>
                  <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">ค่าคอมฝาก</th>
                  <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">ยอดถอนรวม</th>
                  <th className="px-4 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">ค่าคอมถอน</th>
                  <th className="px-5 py-3.5 text-right text-xs font-semibold text-gray-500 dark:text-slate-400 uppercase tracking-wider">รวมค่าคอม</th>
                </tr>
              </thead>
              <tbody>
                {reportRows.map((row, i) => (
                  <tr key={row.bank?.id ?? i} className={`border-b border-gray-50 dark:border-slate-700/60 hover:bg-gray-50/50 dark:hover:bg-slate-700/30 transition-colors ${i % 2 !== 0 ? 'bg-gray-50/30 dark:bg-slate-700/20' : ''}`}>
                    <td className="px-5 py-4">
                      {row.bank ? (
                        <div className="flex items-center gap-3">
                          <span className={`px-2 py-0.5 rounded-md text-xs font-bold ${bankCodeColors[row.bank.bank_code] || 'bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-slate-400'}`}>
                            {row.bank.bank_code}
                          </span>
                          <div>
                            <p className="font-medium text-gray-800 dark:text-slate-100">{row.bank.name}</p>
                            <p className="text-xs text-gray-400 dark:text-slate-500">{row.bank.account_number}</p>
                          </div>
                        </div>
                      ) : (
                        <span className="text-gray-400 dark:text-slate-500 text-xs">ไม่ทราบธนาคาร</span>
                      )}
                    </td>
                    <td className="px-4 py-4 text-right font-medium text-green-600 dark:text-green-400">฿{fmt(row.total_deposit)}</td>
                    <td className="px-4 py-4 text-right font-semibold text-green-700 dark:text-green-300">฿{fmt(row.deposit_commission)}</td>
                    <td className="px-4 py-4 text-right font-medium text-amber-600 dark:text-amber-400">฿{fmt(row.total_withdrawal)}</td>
                    <td className="px-4 py-4 text-right font-semibold text-amber-700 dark:text-amber-300">฿{fmt(row.withdrawal_commission)}</td>
                    <td className="px-5 py-4 text-right font-bold text-gray-800 dark:text-slate-100">฿{fmt(row.total_commission)}</td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-gray-200 dark:border-slate-600 bg-gray-50 dark:bg-slate-700/40">
                  <td className="px-5 py-4 text-xs font-bold text-gray-500 dark:text-slate-400 uppercase tracking-wider">รวมทั้งหมด</td>
                  <td className="px-4 py-4 text-right font-bold text-green-600 dark:text-green-400">฿{fmt(reportSummary.total_deposit)}</td>
                  <td className="px-4 py-4 text-right font-bold text-green-700 dark:text-green-300">฿{fmt(reportSummary.total_deposit_commission)}</td>
                  <td className="px-4 py-4 text-right font-bold text-amber-600 dark:text-amber-400">฿{fmt(reportSummary.total_withdrawal)}</td>
                  <td className="px-4 py-4 text-right font-bold text-amber-700 dark:text-amber-300">฿{fmt(reportSummary.total_withdrawal_commission)}</td>
                  <td className="px-5 py-4 text-right text-base font-bold" style={{ color: 'var(--theme-primary)' }}>
                    ฿{fmt(reportSummary.total_commission)}
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        )}
      </div>

      {/* ตารางธุรกรรม */}
      {/* มือถือ: ชื่อ+จำนวนบรรทัดบน / ยอดฝาก-ถอนบรรทัดล่าง — จอกว้างค่อยดันไปชิดขวาแถวเดียวกัน */}
      <div className="flex flex-col gap-1.5 mb-3 sm:flex-row sm:items-center sm:gap-3">
        <div className="flex items-center gap-2">
          <FontAwesomeIcon icon={faMoneyBillTransfer} style={{ color: 'var(--theme-primary)' }} />
          <h2 className="font-semibold text-gray-800 dark:text-slate-100">ธุรกรรม</h2>
          <span className="text-xs text-gray-400 dark:text-slate-500">{total.toLocaleString()} รายการ</span>
        </div>
        {txSummary && (
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs sm:ml-auto">
            <span>
              <span className="text-green-600 dark:text-green-400 font-semibold">ฝาก ฿{fmt(txSummary.total_credit)}</span>
              <span className="text-gray-400 dark:text-slate-500"> ({txSummary.credit_count.toLocaleString()})</span>
            </span>
            <span>
              <span className="text-amber-600 dark:text-amber-400 font-semibold">ถอน ฿{fmt(txSummary.total_debit)}</span>
              <span className="text-gray-400 dark:text-slate-500"> ({txSummary.debit_count.toLocaleString()})</span>
            </span>
          </div>
        )}
      </div>

      <DataTable
        data={transactions}
        columns={txColumns}
        loading={txLoading}
        keyExtractor={(tx) => tx.id}
        emptyMessage="ไม่มีรายการธุรกรรมในช่วงเวลาที่เลือก"
        striped
        hoverable
        compact
        serverPagination={{
          currentPage: page,
          totalItems: total,
          pageSize,
          pageSizeOptions: [25, 50, 100],
          onPageChange: (p) => fetchTransactions(p, applied.from, applied.to, applied.bankId),
          onPageSizeChange: (size) => {
            setPageSize(size);
            fetchTransactions(1, applied.from, applied.to, applied.bankId, size);
          },
        }}
      />
    </div>
  );
};

export default AgentCommissionDetailPage;
